import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../services/apiService";
import { workflowService } from "../services/workflowService";
import { Workflow } from "../types/workflow";
import { PageHeader, TabNavigation, Tab } from "../components/common";
import { WorkflowsTab, TemplatesTab, CreateTemplateModal } from "../components/emails";
import WorkflowEditModal from "../components/workflow/WorkflowEditModal";
import WorkflowEmailHistory from "../components/workflow/WorkflowEmailHistory";
import { useSubscription } from "../hooks/useSubscription";
import { FiRefreshCw, FiFileText, FiEdit3 } from "react-icons/fi";

// Template type
interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
  content: string;
  created_at: string;
}

// Type-safe icon wrappers
const RefreshIcon = FiRefreshCw as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;
const DraftsIcon = FiEdit3 as React.FC<React.SVGProps<SVGSVGElement>>;

// Delete confirmation modal component
interface DeleteModalProps {
  workflow: Workflow;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  workflow,
  onCancel,
  onConfirm,
  isDeleting,
}) => (
  <div className="su-delete-modal-overlay" onClick={onCancel}>
    <div className="su-delete-modal" onClick={(e) => e.stopPropagation()}>
      <h3 className="su-delete-modal-title">Delete Workflow</h3>
      <p className="su-delete-modal-message">
        Are you sure you want to delete &quot;{workflow.name}&quot;? This action cannot be
        undone.
      </p>
      <div className="su-delete-modal-actions">
        <button
          className="su-delete-modal-cancel"
          onClick={onCancel}
          disabled={isDeleting}
        >
          Cancel
        </button>
        <button
          className="su-delete-modal-confirm"
          onClick={onConfirm}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

const EmailUpdates: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isFree: isFreePlan, loading: subscriptionLoading } = useSubscription();

  // Tab state
  const activeTab = searchParams.get("tab") || "workflows";

  // Data state
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [customTemplates, setCustomTemplates] = useState<any[]>([]);
  
  // Modal state
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<EmailTemplate | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [editMode, setEditMode] = useState<"edit" | "preview">("preview");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  const [emailHistoryWorkflow, setEmailHistoryWorkflow] = useState<Workflow | null>(null);

  // Tab configuration
  const tabs: Tab[] = [
    {
      id: "workflows",
      label: "WORKFLOWS",
      icon: <RefreshIcon className="w-4 h-4" />,
    },
    {
      id: "templates",
      label: "TEMPLATES",
      icon: <FileTextIcon className="w-4 h-4" />,
    },
    {
      id: "drafts",
      label: "DRAFTS",
      icon: <DraftsIcon className="w-4 h-4" />,
    },
  ];

  // Load data
  const loadData = useCallback(async () => {
    if (subscriptionLoading) return;
    try {
      setLoading(true);

      // Fetch workflows (skipped for free plan — endpoint requires Basic+)
      if (!isFreePlan) {
        const allWorkflows = await workflowService.getUserWorkflows();

        // Filter to only show workflows with schedule_config
        const scheduledWorkflows = allWorkflows.filter(
          (w: Workflow) => w.schedule_config
        );
        setWorkflows(scheduledWorkflows);
      }
      
      // Fetch custom templates
      try {
        console.log("[Templates] Fetching custom templates...");
        const templatesRes = await apiService.getEmailTemplates();
        console.log("[Templates] Response:", templatesRes);
        if (templatesRes.success) {
          setCustomTemplates(templatesRes.data || []);
        } else {
          console.error("[Templates] API returned success=false");
          setCustomTemplates([]);
        }
      } catch (err) {
        console.error("[Templates] Error fetching templates:", err);
        setCustomTemplates([]);
      }
      
    } catch (error) {
      console.error("Failed to load emails data:", error);
    } finally {
      setLoading(false);
    }
  }, [isFreePlan, subscriptionLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Tab change handler
  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  // Workflow actions
  const handleCreateWorkflow = () => {
    // Navigate to the new Compose Update page
    navigate("/updates/new");
  };

  const handlePauseResume = async (workflow: Workflow) => {
    try {
      const newEnabled = !workflow.schedule_enabled;
      await workflowService.updateWorkflow(workflow.id, {
        schedule_enabled: newEnabled,
      });
      
      // Update local state
      setWorkflows((prev) =>
        prev.map((w) =>
          w.id === workflow.id ? { ...w, schedule_enabled: newEnabled } : w
        )
      );
    } catch (error) {
      console.error("Failed to toggle workflow:", error);
    }
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    // Create a deep copy to prevent reference issues
    setSelectedWorkflow({ ...workflow });
    setEditMode("edit");
    setShowEditModal(true);
  };

  const handleViewWorkflow = (workflow: Workflow) => {
    // Create a deep copy to prevent reference issues
    setSelectedWorkflow({ ...workflow });
    setEditMode("preview");
    setShowEditModal(true);
  };

  const handleDeleteWorkflow = (workflow: Workflow) => {
    setWorkflowToDelete(workflow);
    setShowDeleteModal(true);
  };

  const handleViewEmails = (workflow: Workflow) => {
    setEmailHistoryWorkflow(workflow);
    setShowEmailHistory(true);
  };

  const confirmDeleteWorkflow = async () => {
    if (!workflowToDelete) return;
    
    try {
      setIsDeleting(true);
      await workflowService.deleteWorkflow(workflowToDelete.id);
      
      // Update local state
      setWorkflows((prev) => prev.filter((w) => w.id !== workflowToDelete.id));
      setShowDeleteModal(false);
      setWorkflowToDelete(null);
    } catch (error) {
      console.error("Failed to delete workflow:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Template actions
  const handleUseTemplate = (templateId: string, isBuiltIn: boolean) => {
    // Navigate to compose update page with template pre-selected
    navigate(`/updates/new?template=${templateId}&builtin=${isBuiltIn}`);
  };

  const handleCreateTemplate = () => {
    setShowCreateTemplateModal(true);
  };

  const handleSaveTemplate = async (template: {
    name: string;
    description: string;
    subject: string;
    content: string;
  }): Promise<void> => {
    console.log("[Templates] Creating template:", template);
    const response = await apiService.createEmailTemplate(template);
    console.log("[Templates] Create response:", response);

    if (!response.success) {
      throw new Error("Failed to create template");
    }

    // Refresh templates list
    await loadData();
  };

  const handleEditTemplate = (templateId: string) => {
    const template = customTemplates.find((t) => t.id === templateId);
    if (template) {
      setTemplateToEdit(template);
      setShowCreateTemplateModal(true);
    }
  };

  const handleUpdateTemplate = async (template: {
    name: string;
    description: string;
    subject: string;
    content: string;
  }): Promise<void> => {
    if (!templateToEdit) return;
    
    await apiService.updateEmailTemplate(templateToEdit.id, template);
    // Refresh templates list
    loadData();
    // Clear edit state
    setTemplateToEdit(null);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }
    
    try {
      await apiService.deleteEmailTemplate(templateId);
      // Refresh templates list
      loadData();
    } catch (error) {
      console.error("Failed to delete template:", error);
    }
  };

  return (
    <div className="max-w-[90rem] mx-auto py-8 px-6 lg:px-8 xl:px-12">
      {/* Page Header */}
      <PageHeader
        title="Emails"
        subtitle="Manage your workflows and templates"
      />

      {/* Tab Navigation */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Tab Content */}
      {activeTab === "workflows" && (
        <WorkflowsTab
          workflows={workflows}
          isLoading={loading}
          isFreePlan={isFreePlan}
          onCreateWorkflow={handleCreateWorkflow}
          onPauseResume={handlePauseResume}
          onEdit={handleEditWorkflow}
          onView={handleViewWorkflow}
          onDelete={handleDeleteWorkflow}
          onViewEmails={handleViewEmails}
        />
      )}

      {activeTab === "templates" && (
        <TemplatesTab
          customTemplates={customTemplates}
          isLoading={loading}
          onUseTemplate={handleUseTemplate}
          onCreateTemplate={handleCreateTemplate}
          onEditTemplate={handleEditTemplate}
          onDeleteTemplate={handleDeleteTemplate}
        />
      )}

      {activeTab === "drafts" && (
        <div className="su-emails-tab-content">
          <div className="su-section">
            <div className="su-section-header">
              <h2 className="su-section-title">Email Drafts</h2>
              <button
                className="su-btn su-btn-purple su-btn-medium"
                onClick={() => navigate("/updates/new")}
              >
                New Draft
              </button>
            </div>
            <div className="su-empty-state">
              <DraftsIcon className="w-12 h-12 text-gray-500 mb-4" />
              <p className="text-gray-400 text-center">
                No drafts yet. Start composing an email to save it as a draft.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edit/View Workflow Modal */}
      {showEditModal && selectedWorkflow && (
        <WorkflowEditModal
          workflow={selectedWorkflow}
          mode={editMode}
          onClose={() => {
            setShowEditModal(false);
            setSelectedWorkflow(null);
          }}
          onUpdated={() => {
            setShowEditModal(false);
            setSelectedWorkflow(null);
            loadData();
          }}
          onDelete={async (workflowId: string) => {
            await workflowService.deleteWorkflow(workflowId);
            setShowEditModal(false);
            setSelectedWorkflow(null);
            setWorkflows((prev) => prev.filter((w) => w.id !== workflowId));
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && workflowToDelete && (
        <DeleteModal
          workflow={workflowToDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setWorkflowToDelete(null);
          }}
          onConfirm={confirmDeleteWorkflow}
          isDeleting={isDeleting}
        />
      )}

      {/* Create/Edit Template Modal */}
      {showCreateTemplateModal && (
        <CreateTemplateModal
          onClose={() => {
            setShowCreateTemplateModal(false);
            setTemplateToEdit(null);
          }}
          onSave={templateToEdit ? handleUpdateTemplate : handleSaveTemplate}
          template={templateToEdit}
          isFreePlan={isFreePlan}
        />
      )}

      {/* Email History Modal */}
      {showEmailHistory && emailHistoryWorkflow && (
        <WorkflowEmailHistory
          workflow={emailHistoryWorkflow}
          onClose={() => {
            setShowEmailHistory(false);
            setEmailHistoryWorkflow(null);
          }}
        />
      )}
    </div>
  );
};

export default EmailUpdates;
